import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMapPin, FiHeart, FiCamera } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { generateSlug } from '../utils/slugify';
import { getImageUrl, getDefaultPropertyImage } from '../utils/imageUtils';

const MobilePropertyCard = ({ property }) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const { user, userFavorites, toggleFavorite } = useContext(AuthContext);

  const agentName = property.postedBy?.name || 'Real Properties';
  const agentInitials = agentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  const isWealth = property.isWealthProperty;
  
  let imgUrl = getDefaultPropertyImage(property.propertyType, property.subCategory);
  if (property.images && property.images.length > 0) {
    imgUrl = getImageUrl(property.images[0], isWealth);
  }

  const isFavorite = userFavorites?.some(fav => 
    typeof fav === 'object' ? fav._id === property._id : fav === property._id
  );

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    await toggleFavorite(property._id);
  };

  const handleCardClick = () => {
    const link = isWealth 
      ? `/wealth-property/${generateSlug(property.title)}/${property._id}` 
      : `/property/${generateSlug(property.title)}/${property._id}`;
    navigate(link);
  };

  const formatPrice = (price) => {
    if (!price) return 'On Request';
    if (price >= 10000000) return `₹ ${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹ ${(price / 100000).toFixed(1)} L`;
    return `₹ ${price.toLocaleString('en-IN')}`;
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden w-full cursor-pointer flex flex-col active:scale-[0.98] transition-transform duration-200"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative w-full aspect-[16/9] bg-gray-100">
        <img 
          src={imgError ? 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80&fm=webp' : imgUrl} 
          onError={() => setImgError(true)}
          alt={property.title} 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        {(!property.images || property.images.length === 0) && !property.coverImage && !property.photo && (
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center z-20">
            <button 
              onClick={(e) => { e.stopPropagation(); handleCardClick(e); }} 
              className="bg-white text-primary px-3 py-1.5 rounded font-bold text-xs md:text-sm shadow-lg flex items-center gap-1.5 hover:bg-gray-100 transition-colors"
            >
              <FiCamera /> Request Image
            </button>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2 left-2 z-10 flex gap-1">
          {property.for === 'Rent' ? (
            <span className="bg-blue-600/90 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Rent</span>
          ) : (
            <span className="bg-primary/90 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Sale</span>
          )}
          {property.status === 'sold' && (
            <span className="bg-red-600/90 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider">Sold</span>
          )}
        </div>
        
        {/* Favorite */}
        <button 
          onClick={handleFavoriteClick}
          className="absolute top-2 right-2 z-10 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm"
        >
          <FiHeart className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-500"} />
        </button>

        {/* Photos Count */}
        <div className="absolute bottom-2 right-2 z-10 bg-black/50 backdrop-blur text-white text-[10px] font-medium px-2 py-1 rounded flex items-center gap-1">
          <FiCamera size={12} /> {property.images?.length || 0}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-grow">
        <h3 className="font-bold text-[15px] text-gray-900 mb-1 line-clamp-1 leading-tight">
          {property.title}
        </h3>
        
        <p className="text-[12px] text-gray-500 mb-2 flex items-center gap-1 line-clamp-1">
          <FiMapPin size={12} className="flex-shrink-0 text-gray-400" /> 
          {property.address?.line1 ? `${property.address.line1}, ` : ''}{property.address?.city}
        </p>

        <div className="mb-2">
          {(() => {
            const total = Number(property.price?.total);
            const areaVal = Number(property.area?.value);
            const areaUnit = property.area?.unit || 'Sq Yards';
            const perUnitValue = property.price?.perUnit ? Number(property.price.perUnit) : (total && areaVal ? Math.round(total / areaVal) : null);
            
            if (!areaVal && !total) return <div className="font-bold text-[16px] text-primary">On Request</div>;
            
            const parts = [];
            if (areaVal) parts.push(<span key="area" className="bg-primary/10 text-primary font-bold px-1.5 py-0.5 rounded text-[11px]">{areaVal} {areaUnit}</span>);
            if (perUnitValue) parts.push(<span key="perUnit" className="bg-primary text-white font-bold px-1.5 py-0.5 rounded text-[11px]">@ ₹{perUnitValue.toLocaleString('en-IN')}/{areaUnit.replace(/s$/i, '')}</span>);
            parts.push(<span key="total" className="bg-primary text-white font-bold px-1.5 py-0.5 rounded text-[11px]">₹{total ? total.toLocaleString('en-IN') : 'On Request'}</span>);
            
            return (
              <div className="flex items-center gap-1 flex-wrap text-[12px]">
                {parts.reduce((prev, curr, i) => [prev, <span key={`sep-${i}`} className="text-gray-400">-</span>, curr])}
              </div>
            );
          })()}
        </div>

        {property.specifications?.bedrooms && (
          <div className="mb-2 flex flex-wrap gap-2">
            <span className="text-[11px] font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
              {property.specifications.bedrooms} BHK
            </span>
          </div>
        )}

        <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-[10px] rounded-full w-6 h-6 flex-shrink-0">
              {agentInitials}
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] font-semibold text-gray-900 block truncate max-w-[100px]">
                {agentName}
              </span>
              <span className="text-[9px] text-gray-500">Platform Verified</span>
            </div>
          </div>
          
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
            className="px-2 py-1 text-[11px] font-semibold bg-primary text-white rounded flex items-center gap-1 hover:bg-orange-600 transition-colors shadow-sm"
          >
            Contact
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobilePropertyCard;
